import { FontAwesome5 } from '@expo/vector-icons';
import { OEText } from '@openeducation/oe-it-components';
import { styled, css, SizeBreakpoint } from '@openeducation/oe-it-themes';
import * as React from 'react';
import { StyleProp, ViewProps, ViewStyle } from 'react-native';

import { useBannerColor } from './use-banner-color';
import type { BannerType } from './use-banner-color';

export interface BannerProps extends ViewProps {
  title?: string;
  message?: string;
  type?: BannerType;
  actions?: React.ReactNode;
  onDismiss?: () => void;
  innerStyle?: StyleProp<ViewStyle>;
}

export const Banner = ({
  title,
  message,
  type = 'warning',
  actions,
  onDismiss,
  style,
  innerStyle,
}: BannerProps) => {
  const { colorType } = useBannerColor(type);
  const [hide, setHide] = React.useState(false);

  if (!colorType) {
    return null;
  }

  const handleDismiss = () => {
    setHide(true);
    onDismiss?.();
  };

  if (hide) {
    return null;
  }
  return (
    <Container
      type={type}
      backgroundColor={colorType.background}
      borderColor={colorType.border}
      style={style}
    >
      <InnerContainer hasDismissButton={!!onDismiss} style={innerStyle}>
        <TextContainer>
          {!!title && (
            <OEText fontSize={16} fontType="medium" color={colorType.text}>
              {title}
            </OEText>
          )}
          {!!message && (
            <OEText fontSize={14} color={colorType.text}>
              {message}
            </OEText>
          )}
        </TextContainer>
        {!!actions && <ActionsContainer>{actions}</ActionsContainer>}
        {!!onDismiss && (
          <CloseButton onPress={handleDismiss}>
            <FontAwesome5 name="times" color={colorType.text} size={16} />
          </CloseButton>
        )}
      </InnerContainer>
    </Container>
  );
};

interface ContainerProps {
  type: BannerType;
  backgroundColor: string;
  borderColor: string;
}

const Container = styled.View<ContainerProps>`
  align-items: center;
  justify-content: center;
  width: 100%;
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-bottom-width: 1px;
  border-bottom-color: ${({ borderColor }) => borderColor};
  flex-direction: column;
`;

type InnerContainerProps = {
  hasDismissButton: boolean;
};

const InnerContainer = styled.View<InnerContainerProps>`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 16px 32px;
  ${({ hasDismissButton }) =>
    hasDismissButton &&
    css`
      padding-right: 48px;
    `}
  width: 100%;
  max-width: ${SizeBreakpoint.XXXL}px;
`;

const TextContainer = styled.View`
  flex-grow: 1;
  margin-right: 16px;
  margin-bottom: 16px;
`;

const CloseButton = styled.TouchableOpacity`
  position: absolute;
  padding: 8px;
  top: 10px;
  right: 20px;
  z-index: 9;
`;

const ActionsContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
`;
